import "./add-address-modal.css";

import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";
import { Form, FormElement } from "@tql/ui-components-react/kendo/form";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { useNavigate } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type {
	AddressBookContactDetailCreate,
	AddressBookCreateBodyV2,
	AddressBookCreateResponseV2,
	AddressBookUpdateBodyV2,
} from "~/shared/schemas/address-book-schema.ts";
import type { ZipCode } from "~/shared/schemas/location-state-schema.ts";
import {
	formatTimeToString,
	getTimeZoneShortName,
} from "~/shared/utils/time-utils.ts";

import { AddressBookPickupDeliveryCard } from "../address-book-address/components/address-book-pickup-delivery-card/address-book-pickup-delivery-card.tsx";
import { AddressFormFields } from "../address-form-fields/address-form-fields.tsx";
import { useAddAddressModal } from "./hooks/use-add-address-modal.ts";

type StopType = "pickup" | "delivery";

type AddAddressModalProps = {
	onClose: () => void;
	customerId: number;
	/**
	 * Optional callback invoked after the address is created. When provided, the
	 * modal does not navigate to the address book edit page, allowing the caller
	 * (e.g. shipment management) to keep the user in their current context. The
	 * submitted body is included so the caller can apply the entered values.
	 */
	onCreated?: (
		result: AddressBookCreateResponseV2,
		body: AddressBookCreateBodyV2,
	) => void;
	/**
	 * When provided, Save does NOT persist to the address book API. Instead the
	 * assembled body is handed back to the caller so it can apply the values
	 * locally (e.g. shipment management updating its stop draft). Takes precedence
	 * over `onCreated`.
	 */
	onSubmitDraft?: (body: AddressBookCreateBodyV2) => void;
	/**
	 * When provided, the matching pickup/delivery stop details (location type,
	 * accessorials, hours, contact, and BOL special instructions) are rendered
	 * inside the modal and saved with the address.
	 */
	stopType?: StopType;
	/**
	 * Prefill values for the form fields. When present the modal renders as an
	 * edit form seeded with these values.
	 */
	initialValues?: Record<string, unknown>;
	/**
	 * When a positive id is supplied the submit updates that existing address book
	 * item instead of creating a new one.
	 */
	addressBookItemId?: number | null;
	/**
	 * Forces the add/edit wording of the title. Defaults to whether an
	 * `addressBookItemId` was supplied. Callers that only edit values locally (via
	 * `onSubmitDraft`) have no address book id but still want the edit title.
	 */
	isEdit?: boolean;
	/**
	 * "shipment" uses shipment management's copy: "Address Line 1"/"Address Line
	 * 2" labels and no required markers on the auto-filled City, State, and
	 * Country fields.
	 */
	variant?: "address-book" | "shipment";
};

// Builds a single contact detail (pickup=1, delivery=2) from the card's form
// fields. It is always returned, even when every field is blank: the address
// book only lists an address for a stop when it has a detail for that stop
// type, so dropping a blank detail would hide the new address from the list.
const buildStopDetail = (
	formValues: Record<string, unknown>,
	prefix: StopType,
): AddressBookContactDetailCreate => {
	const contactName = (formValues[`${prefix}_contactName`] as string) ?? "";
	const contactPhone = (formValues[`${prefix}_contactPhone`] as string) ?? "";
	const contactExtension =
		(formValues[`${prefix}_contactExtension`] as string) ?? "";
	const hoursOpen = formValues[`${prefix}_hoursOpen`] as Date | null;
	const hoursClose = formValues[`${prefix}_hoursClose`] as Date | null;
	const specialInstructions =
		(formValues[`${prefix}_specialInstructions`] as string) ?? "";
	const accessorials =
		(formValues[`${prefix}_accessorials`] as Accessorial[]) ?? [];

	return {
		contactName,
		contactPhone,
		contactExtension,
		hoursOpen: formatTimeToString(hoursOpen) || undefined,
		hoursClose: formatTimeToString(hoursClose) || undefined,
		stopType: prefix === "pickup" ? 1 : 2,
		timeZone: hoursOpen || hoursClose ? getTimeZoneShortName() : undefined,
		specialInstructions: specialInstructions || "",
		accessorials: JSON.stringify(accessorials.map((a) => a.id)),
	};
};

// Maps the create-shaped body onto an update body for an existing address book
// item. The contact detail create shape is a subset of the update shape, so the
// details carry over unchanged.
const toUpdateBody = (
	body: AddressBookCreateBodyV2,
	addressBookItemId: number,
): AddressBookUpdateBodyV2 => ({
	addressBookItemId,
	customerId: body.customerId,
	companyName: body.companyName,
	address1: body.address1,
	address2: body.address2,
	postalCode: body.postalCode,
	city: body.city,
	stateProvince: body.stateProvince,
	country: body.country,
	isValidated: body.isValidated,
	details: body.details,
});

// Lists Commercial first, matching the location type options on the shipment stop.
const sortCommercialFirst = (options: Accessorial[]): Accessorial[] =>
	[...options].sort(
		(a, b) => Number(b.name === "Commercial") - Number(a.name === "Commercial"),
	);

// Prevent Enter from submitting the form while a picker/dropdown popup is open
// (e.g. selecting a time in the hours TimePicker). Kendo renders open popups
// in a portal outside the form and marks the shown container with
// "k-animation-container-shown", so while one is visible an Enter keypress
// should confirm the popup selection rather than save the address.
const handleFormKeyDown = (
	event: React.KeyboardEvent<HTMLFormElement>,
): void => {
	if (event.key !== "Enter") {
		return;
	}
	if (document.querySelector(".k-animation-container-shown")) {
		event.preventDefault();
	}
};

export const AddAddressModal = ({
	onClose,
	customerId,
	onCreated,
	onSubmitDraft,
	stopType,
	initialValues,
	addressBookItemId,
	isEdit,
	variant = "address-book",
}: AddAddressModalProps) => {
	const {
		createAddressBookItem,
		updateAddressBookItem,
		isSubmitting,
		groupedAccessorials,
	} = useAddAddressModal();
	const navigate = useNavigate();

	const hasAddressBookItemId =
		typeof addressBookItemId === "number" && addressBookItemId > 0;
	const showEditTitle = isEdit ?? hasAddressBookItemId;
	const isShipmentVariant = variant === "shipment";

	const submitAddress = (body: AddressBookCreateBodyV2) => {
		const save: Promise<AddressBookCreateResponseV2> =
			hasAddressBookItemId && addressBookItemId
				? updateAddressBookItem(toUpdateBody(body, addressBookItemId)).then(
						() => ({ addressBookItemId }),
					)
				: createAddressBookItem(body);

		save
			.then((result) => {
				if (onCreated) {
					onCreated(result, body);
				} else if (result?.addressBookItemId) {
					navigate(
						routes.addressBookEdit
							.replace(":customerId", String(customerId))
							.replace(":addressBookItemId", String(result.addressBookItemId)),
					);
				}
				onClose();
			})
			.catch(() => {});
	};

	const handleFormSubmit = (formValues: Record<string, unknown>): void => {
		const addressPostalCode = formValues["addressPostalCode"] as
			| ZipCode
			| undefined;

		const details: AddressBookContactDetailCreate[] = stopType
			? [buildStopDetail(formValues, stopType)]
			: [];

		const body: AddressBookCreateBodyV2 = {
			customerId,
			companyName: (formValues["companyName"] as string)?.trim() ?? "",
			address1: (formValues["address1"] as string)?.trim() ?? "",
			address2: (formValues["address2"] as string)?.trim() ?? "",
			postalCode: addressPostalCode?.postalCode ?? "",
			city: (formValues["city"] as string)?.trim() ?? "",
			stateProvince: (formValues["state"] as string)?.trim() ?? "",
			country: (formValues["country"] as string)?.trim() ?? "",
			isValidated: false,
			createdByCustomerId: customerId,
			details,
		};

		if (onSubmitDraft) {
			onSubmitDraft(body);
			onClose();
			return;
		}

		submitAddress(body);
	};

	return (
		<TraxModal
			className={"add-address-modal"}
			onClose={onClose}
			title={showEditTitle ? "Edit Address" : "Add Address"}
			width={stopType ? "60%" : "35%"}
		>
			<LoadingSpinner isVisible={isSubmitting} />
			<Form
				ignoreModified={true}
				initialValues={{
					...(stopType ? { [`${stopType}_accessorials`]: [] } : {}),
					...initialValues,
				}}
				onSubmit={handleFormSubmit}
				render={(formRenderProps: FormRenderProps) => {
					return (
						<FormElement onKeyDown={handleFormKeyDown}>
							<AddressFormFields
								addressLineLabels={isShipmentVariant}
								formRenderProps={formRenderProps}
								showAutoFilledRequiredMarkers={!isShipmentVariant}
							/>

							{stopType ? (
								<AddressBookPickupDeliveryCard
									fieldPrefix={stopType}
									locationTypeOptions={sortCommercialFirst(
										groupedAccessorials?.locationType[stopType] ?? [],
									)}
									onChange={(value: Accessorial[]) =>
										formRenderProps.onChange(`${stopType}_accessorials`, {
											value,
										})
									}
									serviceOptions={
										groupedAccessorials?.selectable[stopType] ?? []
									}
									type={stopType}
									value={
										formRenderProps.valueGetter(
											`${stopType}_accessorials`,
										) as Accessorial[]
									}
									variant={"modal"}
								/>
							) : null}

							<DialogActionsBar layout={"end"}>
								<Button
									disabled={isSubmitting}
									formAction={"none"}
									onClick={onClose}
								>
									Cancel
								</Button>
								<Button
									disabled={isSubmitting || !formRenderProps.allowSubmit}
									formAction={"submit"}
									variant={"primary"}
								>
									{isSubmitting ? "Saving..." : "Save"}
								</Button>
							</DialogActionsBar>
						</FormElement>
					);
				}}
			/>
		</TraxModal>
	);
};
